import React, { useState } from 'react';
import { Menu, X, Heart, Stethoscope, Calendar, CheckCircle, Star, Users, Award } from 'lucide-react';
import './SiteProfissional.css';

const SiteProfissional: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navigateTo = (page: string) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  const handleWhatsApp = (telefone: string) => {
    if (!telefone) {
      alert('Telefone não disponível.');
      return;
    }
    
    // Remove todos os caracteres não numéricos
    let numeroLimpo = telefone.replace(/\D/g, '');
    
    if (numeroLimpo.length < 10) {
      alert('Número de telefone inválido.');
      return;
    }
    
    // Adiciona código do país se não tiver
    if (numeroLimpo.length === 11 && numeroLimpo.startsWith('32')) {
      numeroLimpo = '55' + numeroLimpo; // Adiciona código do Brasil
    } else if (numeroLimpo.length === 10 && numeroLimpo.startsWith('32')) {
      numeroLimpo = '5532' + numeroLimpo.substring(2); // Adiciona código do Brasil e 9
    }
    
    // Detecta se é mobile e usa URL apropriada
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const url = isMobileDevice 
      ? `https://api.whatsapp.com/send?phone=${numeroLimpo}`
      : `https://web.whatsapp.com/send?phone=${numeroLimpo}`;
    
    window.open(url, '_blank');
  };

  const renderHome = () => (
    <div className="page-content">
      {/* Hero / Abertura */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content-centered">
            <img 
              src="/logo3.png" 
              alt="Regiane Faia" 
              className="hero-logo"
              style={{height: '240px', width: 'auto', objectFit: 'contain', marginBottom: '1.5rem'}}
            />
            <h2>Nutricionista & Educadora Física</h2>
            <p>Transforme sua saúde com acompanhamento profissional personalizado. Resultados reais, mudanças duradouras.</p>
            <div className="hero-cta-buttons">
              <button 
                className="cta-button primary large"
                onClick={() => handleWhatsApp('32999125225')}
                title="Abrir WhatsApp"
              >
                <span className="cta-icon">💬</span>
                Falar no WhatsApp
              </button>
            </div>
            <div className="hero-trust-indicators">
              <div className="trust-item">
                <span className="trust-number">20000+</span>
                <span className="trust-label">Pacientes Atendidas</span>
              </div>
              <div className="trust-item">
                <span className="trust-number">15+</span>
                <span className="trust-label">Anos de Experiência</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Mim */}
      <section className="about-me-section">
        <div className="container">
          <h2>Quem sou eu</h2>
          <div className="about-me-grid">
            <div className="about-me-image">
              <img src="/NAZ_8282.jpg" alt="Regiane Faia - Nutricionista" />
            </div>
            <div className="about-me-content">
              <p className="about-me-intro">
                Sou <strong>Regiane Faia</strong>, nutricionista, educadora física e mestra em Imunologia e Microbiologia. Minha jornada na saúde começou como atleta de taekwondo, onde descobri o poder transformador da nutrição na performance e no bem-estar.
              </p>
              <p className="about-me-story">
                Como mãe e profissional, sei como é desafiador encaixar uma alimentação saudável na correria do dia a dia. Entre trabalho, família e compromissos, muitas vezes deixamos nossa saúde em segundo plano. É por isso que desenvolvi métodos práticos e realistas, que se adaptam à sua rotina, sem complicações ou receitas impossíveis de seguir.
              </p>
              
              <div className="about-me-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">🎓</div>
                  <div className="highlight-content">
                    <h4>Formação</h4>
                    <p>Nutricionista • Educadora Física • Mestra em Imunologia e Microbiologia</p>
                  </div>
                </div>
                
                <div className="highlight-item">
                  <div className="highlight-icon">💝</div>
                  <div className="highlight-content">
                    <h4>Missão</h4>
                    <p>Transformar vidas através da nutrição e da atividade física com base científica e cuidado humanizado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios / Por que escolher */}
      <section className="benefits-section">
        <div className="container">
          <h2>Por que escolher nosso acompanhamento?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <Heart className="benefit-icon" />
              <h3>Cuidado Personalizado</h3>
              <p>Plano nutricional e físico único para seus objetivos, com acompanhamento personal quando necessário</p>
            </div>
            <div className="benefit-card">
              <Stethoscope className="benefit-icon" />
              <h3>Acompanhamento Profissional</h3>
              <p>Monitoramento constante com ajustes em tempo real</p>
            </div>
            <div className="benefit-card">
              <Calendar className="benefit-icon" />
              <h3>Flexibilidade Total</h3>
              <p>Atendimento presencial e online, no seu tempo</p>
            </div>
            <div className="benefit-card">
              <CheckCircle className="benefit-icon" />
              <h3>Resultados Comprovados</h3>
              <p>Metodologia testada com centenas de pacientes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dores que eu resolvo */}
      <section className="problems-section">
        <div className="container">
          <h2>Problemas que eu resolvo</h2>
          
          <div className="problems-category">
            <h3>Para Homens e Mulheres</h3>
            <div className="problems-grid">
              <div className="problem-card">
                <div className="problem-icon">⚖️</div>
                <h4>Sobrepeso e Obesidade</h4>
                <p>Emagrecimento saudável e sustentável</p>
              </div>
              <div className="problem-card">
                <div className="problem-icon">🩺</div>
                <h4>Diabetes e Resistência Insulínica</h4>
                <p>Controle glicêmico através da alimentação</p>
              </div>
              <div className="problem-card">
                <div className="problem-icon">❤️</div>
                <h4>Hipertensão</h4>
                <p>Redução da pressão arterial naturalmente</p>
              </div>
              <div className="problem-card">
                <div className="problem-icon">🧪</div>
                <h4>Colesterol Alto</h4>
                <p>Melhora do perfil lipídico</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Mini apresentação dos serviços */}
      <section className="services-preview">
        <div className="container">
          <h2>Minhas Especialidades</h2>
          <p className="services-subtitle">Cuidado integral para sua saúde e performance</p>
          <div className="services-grid-six">
            <div className="service-card-mini">
              <div className="service-icon">🥗</div>
              <h3>Consulta Nutricional</h3>
              <p>Avaliação completa e plano alimentar personalizado para seus objetivos de saúde e bem-estar</p>
            </div>
            <div className="service-card-mini">
              <div className="service-icon">🏃‍♀️</div>
              <h3>Nutrição + Personal Trainer Presencial</h3>
              <p>Acompanhamento completo com nutrição personalizada e treinos presenciais para máxima performance</p>
            </div>
            <div className="service-card-mini">
              <div className="service-icon">💻</div>
              <h3>Nutrição + Personal Trainer Remoto</h3>
              <p>Plano alimentar e treinos online com acompanhamento integral via app e videochamadas</p>
            </div>
            <div className="service-card-mini">
              <div className="service-icon">🌸</div>
              <h3>Saúde da Mulher</h3>
              <p>Acompanhamento nutricional especializado para todas as fases da vida feminina com abordagem hormonal</p>
            </div>
            <div className="service-card-mini">
              <div className="service-icon">⚖️</div>
              <h3>Emagrecimento Saudável</h3>
              <p>Perda de peso sustentável com reeducação alimentar, sem dietas restritivas ou efeito sanfona</p>
            </div>
            <div className="service-card-mini">
              <div className="service-icon">🌺</div>
              <h3>Endometriose</h3>
              <p>Protocolo nutricional anti-inflamatório específico para reduzir sintomas e melhorar qualidade de vida</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );

  const renderSobre = () => (
    <div className="page-content">
      <section className="identity-section">
        <div className="container">
          <div className="identity-grid">
            <div className="identity-image">
              <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=500&fit=crop&crop=face" alt="Regiane Faia" />
            </div>
            <div className="identity-content">
              <h1>Regiane Faia</h1>
              <h2>Nutricionista CRN 12345 | Educadora Física CREF 67890</h2>
              <p>Especialista em nutrição clínica e saúde da mulher, com mais de 8 anos transformando vidas através da alimentação e exercício físico.</p>
              <div className="credentials">
                <div className="credential">
                  <Award className="credential-icon" />
                  <span>Pós-graduada em Nutrição Clínica</span>
                </div>
                <div className="credential">
                  <Star className="credential-icon" />
                  <span>Especialista em Saúde da Mulher</span>
                </div>
                <div className="credential">
                  <Users className="credential-icon" />
                  <span>+500 pacientes atendidos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderServicos = () => (
    <div className="page-content">
      <section className="services-list">
        <div className="container">
          <h1>Meus Serviços</h1>
          <p>Soluções completas para sua transformação</p>
        </div>
      </section>
    </div>
  );

  const renderAreaPaciente = () => (
    <div className="page-content">
      <section className="login-section">
        <div className="container">
          <h1>Área do Paciente</h1>
          <div className="login-form">
            <h2>Acesso Restrito</h2>
            <form>
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="seu@email.com" />
              </div>
              <div className="form-group">
                <label>Senha</label>
                <input type="password" placeholder="Sua senha" />
              </div>
              <button type="submit" className="cta-button full-width">Entrar</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home':
        return renderHome();
      case 'sobre':
        return renderSobre();
      case 'servicos':
        return renderServicos();
      case 'area-paciente':
        return renderAreaPaciente();
      default:
        return renderHome();
    }
  };

  return (
    <div className="site-profissional">
      <header className="header bg-white border-bottom shadow-sm p-3">
        <div className="container-fluid">
          <div className="d-flex align-items-center w-100">
            <div className="flex-grow-1 d-flex justify-content-start">
              <img 
                src="/logo2.png" 
                alt="Logo" 
                className="d-none d-md-block"
                style={{height: '95px', width: 'auto', objectFit: 'contain'}}
              />
              <img 
                src="/logo2.png" 
                alt="Logo" 
                className="mobile-logo d-md-none"
                style={{height: '90px', width: '100%', objectFit: 'contain'}}
              />
            </div>
            
            <div className="ms-auto">
              <div className="position-relative d-none d-md-block">
                <button 
                  type="button"
                  className="user-card d-flex align-items-center gap-2 bg-light p-2 rounded border"
                  style={{cursor: 'pointer', border: 'none'}}
                >
                  <div className="user-avatar d-flex align-items-center justify-content-center bg-success text-white rounded-circle" style={{width: '32px', height: '32px'}}>
                    <Users size={18} />
                  </div>
                  <div className="user-info">
                    <div className="user-name fw-semibold text-dark" style={{fontSize: '0.8rem'}}>Área do Paciente</div>
                    <div className="user-email text-muted" style={{fontSize: '0.7rem'}}>Em breve</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <>
          <div className="menu-overlay" onClick={toggleMenu}></div>
          <nav className="mobile-menu">
            <button 
              className={`menu-item ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => navigateTo('home')}
            >
              Home
            </button>
            <button 
              className={`menu-item ${currentPage === 'sobre' ? 'active' : ''}`}
              onClick={() => navigateTo('sobre')}
            >
              Sobre
            </button>
            <button 
              className={`menu-item ${currentPage === 'servicos' ? 'active' : ''}`}
              onClick={() => navigateTo('servicos')}
            >
              Serviços
            </button>
            <button 
              className={`menu-item ${currentPage === 'area-paciente' ? 'active' : ''}`}
              onClick={() => navigateTo('area-paciente')}
            >
              Área do Paciente
            </button>
          </nav>
        </>
      )}

      <main className="site-main">
        {renderCurrentPage()}
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Regiane Faia</h3>
              <p>Nutricionista & Educadora Física</p>
            </div>
            <div className="footer-section">
              <h3>Contato</h3>
              <p>
                WhatsApp: 
                <button 
                  type="button"
                  className="whatsapp-button" 
                  onClick={() => handleWhatsApp('32999125225')}
                  title="Abrir WhatsApp"
                >
                  (32) 99912-5225
                </button>
              </p>
              <p>Email: regianefaia@gmail.com</p>
            </div>
            <div className="footer-section">
              <h3>Redes Sociais</h3>
              <p>
                <a 
                  href="https://instagram.com/reg_faia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{color: 'inherit', textDecoration: 'none'}}
                >
                  Instagram: @reg_faia
                </a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Regiane Faia. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteProfissional;
